import newCommFreeFlyers from '@/assets/new_comm_free_flyers.svg';
import newSchoolFlyers from '@/assets/new_school_flyers.svg';
import { FlyerType } from '@/config/constants';
import { cx } from '@emotion/css';
import { memo, useMemo, useRef, useState } from 'react';
import SwiperCore, { Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';

import { FadeInWrapper, LiveFeedback } from '@/components';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { BodyLargeMidEmphasis, TitleMediumEmphasized } from '@/design-system';
import { Institution } from '@/graphql/types/Institution';
import { useLiveFeedback, useNavigateWithTracking } from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import CardInfo from '../CardInfo/CardInfo';
import styles from './Card.styles';

interface Props {
  variant: FlyerType.school | FlyerType.community;
  list: Institution[];
  districtId?: string;
}

SwiperCore.use([Pagination]);

type FlyerboardTitles = 'new' | 'upcoming' | 'all';

const Card: React.FC<Props> = ({ variant, list, districtId }) => {
  const { track } = useAnalytics();
  const navigateWithTracking = useNavigateWithTracking();
  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 2000 });
  const imageAlt = {
    [FlyerType.school]: 'Illustration of two people talking near a school building and playground, representing a welcoming school community environment.',
    [FlyerType.community]: 'Illustration of children playing soccer with a coach in a community park, showing active engagement and sports in the community.'
  }
  const slidesPerView = useMemo(() => list.length > 1 ? 1.08 : 1, [list]);
  const spaceBetween = useMemo(() => slidesPerView === 1 ? 0 : 10, [slidesPerView]);
  const swiperRef = useRef<SwiperCore | null>(null);

  const [activeSlide, setActiveSlide] = useState<number>(1);

  const handlePaginationClick = (index: number) => {
    swiperRef.current?.slideTo(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowLeft') {
      swiperRef.current?.slideTo(index - 1);
    } else if (event.key === 'ArrowRight') {
      swiperRef.current?.slideTo(index + 1);
    }
  };

  const renderCustomPagination = () => {
    if (list.length <= 1) return null;
    return (
      <ul className={styles.customPagination}>
        {list.map((_, index) => (
          <li key={index}>
            <button
              type="button"
              role="button"
              tabIndex={0}
              className={cx(styles.paginationButton, {
                [styles.paginationButtonActive]: index + 1 === activeSlide,
              })}
              aria-label={`Slide ${index + 1}`}
              aria-current={index + 1 === activeSlide ? 'true' : undefined}
              onClick={() => handlePaginationClick(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <span className="sr-only">{`Slide ${index + 1}`}</span>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const handleSlideChange = (core: SwiperCore) => {
    const currentSlide = list[core.realIndex];
    const newActiveIndex = core.activeIndex + 1;
    setActiveSlide(newActiveIndex);
    setFeedbackMessage(`Showing slide ${newActiveIndex} of ${list.length}`);
    track({
      category: `home_page:${variant}:carousel`,
      action: GenericTrackTypes.SLIDE,
      label: currentSlide.institutionName,
    });
  };

  const goToFlyers = (title: string, id: string) => {
    const titleLowercase = title.toLowerCase();

    const routeMap: Record<FlyerboardTitles, string> = {
      new: `/explore/new?audienceId=${id}&tab=${variant}`,
      upcoming: `/upcoming?audienceId=${id}`,
      all: `/explore/all?audienceId=${id}&tab=${variant}`,
    };

    const flyerboardUrl = routeMap[titleLowercase as FlyerboardTitles];
    navigateWithTracking(flyerboardUrl, {
      event_from: TrackingEvents.browseContent,
      event_detail_from: EventDetails.browseContentPage('home', variant),
      stage_from: TrackingStage.discovery
    });
  };

  return (
    <FadeInWrapper
      className={styles.container}
      data-testid={`${variant}CardContainer`}
    >
      <div
        aria-roledescription="carousel"
        aria-label={`Subscription carousel: ${variant}`}
        aria-labelledby={`carousel-title-${variant}`}
      >
        <LiveFeedback message={feedbackMessage} />
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          simulateTouch={true}
          modules={[Pagination]}
          slidesPerView={slidesPerView}
          spaceBetween={spaceBetween}
          centeredSlides={false}
          className={styles.swiper}
          onSlideChange={handleSlideChange}
          pagination={{
            el: '.custom-pagination',
            clickable: true,
          }}
          role="list"
        >
          {list.map(({ institutionId, institutionName, informations }, index) => {

            const slideLabel = `slide-${index + 1}-label`;
            const slideKey = `slide-${institutionName}`;
            return (
              <li key={slideKey} role="listitem">
                <SwiperSlide
                  id={`slide-${index + 1}`}
                  key={slideKey}
                  aria-roledescription="slide"
                  aria-labelledby={slideLabel}
                  className={styles.variant[variant]}
                  data-testid="slideItem"
                >
                  <span id={slideLabel} className="sr-only">
                    Slide {index + 1} of {list.length}
                  </span>
                  <BodyLargeMidEmphasis className={styles.cardTitle} data-testid="cardName">
                    {variant}
                  </BodyLargeMidEmphasis>
                  <div>
                    <TitleMediumEmphasized data-testid="slideItemName" className={styles.title}>
                      {institutionName}
                    </TitleMediumEmphasized>
                  </div>
                  <div className={styles.cardInfoList}>
                    {informations.map(({ name, amount, frequency }) =>
                      name === 'Upcoming' ? null : (
                        <CardInfo
                          key={name}
                          id={institutionId}
                          title={name}
                          amount={amount}
                          frequency={frequency}
                          goToFlyers={goToFlyers}
                          dataTestId={`${name}Flyers`}
                        />
                      )
                    )}
                  </div>
                  <div>
                    <img
                      src={variant === FlyerType.school ? newSchoolFlyers : newCommFreeFlyers}
                      alt={imageAlt[variant]}
                      className={styles.cardIllustration}
                      width="100%"
                      height="100%"
                      loading="lazy"
                    />
                  </div>
                </SwiperSlide>
              </li>
            )
          })}
        </Swiper>
        {renderCustomPagination()}
      </div>
    </FadeInWrapper>
  );
};

export default memo(Card);
